import { apiFetch } from "./client";
import type {
  WorkflowStatus,
  WorkflowNode,
  WorkflowEdge,
  WorkflowRunStatus,
  WorkflowAwaitType,
  CreateAgentWorkflow,
  UpdateAgentWorkflow,
  StartWorkflowRun,
} from "@proclaw/shared";

/** Row shape returned by the list endpoint (no nodes/edges — fetched per detail). */
export interface WorkflowListRow {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  status: WorkflowStatus;
  agentCount: number;
  lastRunAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Full workflow including canvas state — returned by GET /:id */
export interface Workflow extends WorkflowListRow {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/** A single validation issue from the shared graph validator. */
export interface WorkflowGraphIssue {
  level: "error" | "warning";
  code: string;
  message: string;
  nodeId?: string;
  edgeId?: string;
}

export interface WorkflowValidation {
  ok: boolean;
  errors: WorkflowGraphIssue[];
  warnings: WorkflowGraphIssue[];
}

export const workflowsApi = {
  list: (companyId: string) =>
    apiFetch<WorkflowListRow[]>(`/companies/${companyId}/workflows`),

  /** Server-side graph validation (errors + warnings) for the canvas. */
  validate: (workflowId: string) =>
    apiFetch<WorkflowValidation>(`/workflows/${workflowId}/validate`),

  /** Workflows that reference this agent (have an agent node with this id). */
  listForAgent: (agentId: string) =>
    apiFetch<WorkflowListRow[]>(`/agents/${agentId}/workflows`),

  get: (workflowId: string) =>
    apiFetch<Workflow>(`/workflows/${workflowId}`),

  create: (companyId: string, data: CreateAgentWorkflow) =>
    apiFetch<Workflow>(`/companies/${companyId}/workflows`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (workflowId: string, data: UpdateAgentWorkflow) =>
    apiFetch<Workflow>(`/workflows/${workflowId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (workflowId: string) =>
    apiFetch<void>(`/workflows/${workflowId}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------
// Workflow runs
// ---------------------------------------------------------------------

export interface WorkflowRunRow {
  id: string;
  workflowId: string;
  status: WorkflowRunStatus;
  assignedAgentId: string | null;
  assignedAgentName?: string | null;
  currentStepId: string | null;
  awaitingType: WorkflowAwaitType | null;
  triggerSource?: string | null;
  retryAt?: string | null;
  error?: { message?: string; stepId?: string; details?: unknown } | null;
  startedBy: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface WorkflowRun {
  id: string;
  workflowId: string;
  companyId: string;
  assignedAgentId: string | null;
  status: WorkflowRunStatus;
  currentStepId: string | null;
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  awaitingType: WorkflowAwaitType | null;
  awaitingCorrelationId: string | null;
  awaitingUntil: string | null;
  triggerSource?: string | null;
  sessionId?: string | null;
  retryAt?: string | null;
  error: { message?: string; stepId?: string; attempts?: number; details?: unknown } | null;
  startedBy: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface WorkflowRunStep {
  id: string;
  runId: string;
  stepId: string;
  nodeType: "trigger" | "agent" | "condition" | "action";
  status: "running" | "awaiting" | "completed" | "failed" | "skipped";
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  branchTaken: string | null;
  error: { reason?: string; details?: unknown } | null;
  attempt: number;
  // Node config captured at execution time (survives later workflow edits).
  nodeSnapshot?: Record<string, unknown> | null;
  // Awaiting-step resume context.
  resumedAt?: string | null;
  resumeSource?: string | null;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  // Joined from the workflow definition by the server.
  nodeLabel: string | null;
}

export interface WorkflowTranscriptMessage {
  role: string;
  content: string;
  createdAt: string;
  metadata?: { source?: string; model?: string; usage?: unknown } | null;
}

export interface WorkflowRunDetail {
  run: WorkflowRun;
  workflow: { id: string; name: string; nodes: WorkflowNode[] } | null;
  steps: WorkflowRunStep[];
  assignedAgent?: { id: string; name: string } | null;
  transcript?: WorkflowTranscriptMessage[];
}

export interface CancelWorkflowRunSnapshot {
  steps: Array<{
    stepId: string;
    nodeType: WorkflowRunStep["nodeType"];
    status: WorkflowRunStep["status"];
    inputs?: Record<string, unknown>;
    outputs?: Record<string, unknown>;
    nodeSnapshot?: Record<string, unknown> | null;
    startedAt?: string | null;
    completedAt?: string | null;
    durationMs?: number | null;
  }>;
}

/** Row returned by GET /agents/:agentId/workflow-runs — joined workflowName. */
export interface AgentWorkflowRunRow extends WorkflowRunRow {
  workflowName: string;
}

export const workflowRunsApi = {
  listForWorkflow: (workflowId: string, limit = 50, status?: string) =>
    apiFetch<WorkflowRunRow[]>(
      `/workflows/${workflowId}/runs?limit=${limit}${
        status ? `&status=${encodeURIComponent(status)}` : ""
      }`,
    ),

  /** Recent runs assigned to this agent (joined with workflow name). */
  listForAgent: (agentId: string, limit = 50) =>
    apiFetch<AgentWorkflowRunRow[]>(
      `/agents/${agentId}/workflow-runs?limit=${limit}`,
    ),

  get: (runId: string) => apiFetch<WorkflowRunDetail>(`/workflow-runs/${runId}`),

  start: (workflowId: string, body: StartWorkflowRun = {}) =>
    apiFetch<WorkflowRun & { workflow: WorkflowRunDetail["workflow"] }>(
      `/workflows/${workflowId}/run`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),

  cancel: (runId: string, snapshot?: CancelWorkflowRunSnapshot) =>
    apiFetch<{ ok: boolean }>(`/workflow-runs/${runId}/cancel`, {
      method: "POST",
      body: snapshot ? JSON.stringify({ snapshot }) : undefined,
    }),
};
